/**
 * Archives on this computer, in the desktop app (ADR-0040): Outlook data
 * files attached as their own folder trees, and archives mail is moved into
 * off the server. Only the app's engine answers `/api/mail/archives`, so in a
 * browser none of this shows.
 */
import { useState } from 'react';
import { archiveRoot } from 'zitimail-shared-types';
import type { LocalArchive } from 'zitimail-shared-types';

import { ChevronRightIcon } from '../components/icons';

const WARNED = 'zitimail.archive-move-warned';
const FOLDED = 'zitimail.archives-folded';

function readFolded(): ReadonlySet<string> {
  try {
    const kept: unknown = JSON.parse(window.localStorage.getItem(FOLDED) ?? '[]');
    return new Set(Array.isArray(kept) ? kept.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Which archives show only their heading, kept on this computer: with
 * several Outlook files open, their folders would otherwise fill the list.
 */
export function useFoldedArchives(): [ReadonlySet<string>, (id: string) => void] {
  const [folded, setFolded] = useState(readFolded);
  const toggle = (id: string) =>
    setFolded((now) => {
      const next = new Set(now);
      if (!next.delete(id)) {
        next.add(id);
      }
      try {
        window.localStorage.setItem(FOLDED, JSON.stringify([...next]));
      } catch {
        // Folded until the app is restarted.
      }
      return next;
    });
  return [folded, toggle];
}

/**
 * Asks before mail first leaves the server for an archive: it is then only
 * on this computer. Asked once; the warning stays in the help text.
 */
export function confirmMoveOff(archive: string, count: number): boolean {
  let warned = false;
  try {
    warned = window.localStorage.getItem(WARNED) === '1';
  } catch {
    // Without storage it asks every time, which is the safe side.
  }
  const what = count === 1 ? 'This conversation' : `These ${count} conversations`;
  if (warned) {
    return true;
  }
  const ok = window.confirm(
    `${what} will be moved off the server into ${archive}, on this computer.\n\n` +
      'Mail in an archive is only on this computer: not in webmail or on your phone, and not in ' +
      "your organisation's backups. If this computer is lost or its disk fails without a backup, " +
      'that mail is gone.\n\nMove it?',
  );
  if (ok) {
    try {
      window.localStorage.setItem(WARNED, '1');
    } catch {
      // Asked again next time.
    }
  }
  return ok;
}

/** A line under an archive's name: converting, what it holds, or what is wrong. */
export function archiveStatus(archive: LocalArchive): string {
  switch (archive.state) {
    case 'converting': {
      const p = archive.progress;
      if (!p || p.expected === 0) {
        return 'Opening the Outlook data file…';
      }
      return `Converting: ${p.converted.toLocaleString()} of ${p.expected.toLocaleString()}`;
    }
    case 'failed':
    case 'missing':
      return archive.problem ?? 'It cannot be opened.';
    default: {
      const left = archive.report?.failed ?? 0;
      const held = `${archive.messages.toLocaleString()} ${archive.messages === 1 ? 'item' : 'items'}`;
      return left > 0 ? `${held}; ${left.toLocaleString()} could not be read` : held;
    }
  }
}

/** The heading of one archive's folders, with what can be done to it. */
export function ArchiveHeading({
  archive,
  open,
  onToggle,
  onNewFolder,
  onRename,
  onDetach,
  onMenu,
}: {
  archive: LocalArchive;
  /** Its folders are shown under it. */
  open: boolean;
  onToggle: () => void;
  onNewFolder: (parent: string) => void;
  onRename: (archive: LocalArchive) => void;
  onDetach: (archive: LocalArchive) => void;
  /** Right-click: the same actions in a menu, at the pointer. */
  onMenu: (archive: LocalArchive, x: number, y: number) => void;
}): JSX.Element {
  const status = archiveStatus(archive);
  const broken = archive.state === 'failed' || archive.state === 'missing';
  return (
    <div
      className="archive-head"
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(archive, e.clientX, e.clientY);
      }}
    >
      <button
        type="button"
        className="folder-group archive-toggle"
        aria-expanded={open}
        title={`On this computer: ${archive.file}`}
        onClick={onToggle}
      >
        <ChevronRightIcon className={`archive-chevron${open ? ' open' : ''}`} />
        <span>{archive.name}</span>
      </button>
      <div className={`archive-status${broken ? ' warn' : ''}`} role={broken ? 'alert' : undefined}>
        {status}
      </div>
      <div className="archive-actions">
        {archive.state === 'ready' && open && (
          <button
            type="button"
            className="text-btn"
            onClick={() => onNewFolder(archiveRoot(archive.id))}
          >
            New folder
          </button>
        )}
        {archive.state !== 'converting' && (
          <button type="button" className="text-btn" onClick={() => onRename(archive)}>
            Rename
          </button>
        )}
        <button
          type="button"
          className="text-btn"
          title="Stop showing it here; its file stays on this computer"
          onClick={() => onDetach(archive)}
        >
          {archive.state === 'converting' ? 'Stop' : 'Close'}
        </button>
      </div>
    </div>
  );
}

/** Opening an Outlook data file, or making a new archive. */
export function ArchiveTools({
  onOpen,
  onNew,
}: {
  onOpen: () => void;
  onNew: () => void;
}): JSX.Element {
  return (
    <div className="archive-tools">
      <div className="folder-group">On this computer</div>
      <button type="button" className="folder" onClick={onOpen}>
        Open Outlook data file…
      </button>
      <button type="button" className="folder" onClick={onNew}>
        New archive…
      </button>
    </div>
  );
}
